import { safeJsonParse } from '../../../../../../utils/jsonRepairUtils';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return undefined;
}

export function getFirstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const resolved = getString(value)?.trim();
    if (resolved) {
      return resolved;
    }
  }
  return undefined;
}

export interface Tag {
  key: string;
  value: string;
}

function stringifyTagValue(value: unknown): string {
  const str = getString(value);
  if (str !== undefined) {
    return str;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (value === null || value === undefined) {
    return '';
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function parsePrimitiveTagValue(
  value: unknown,
): Record<string, unknown> | null {
  const normalized = getString(value)?.trim();
  if (!normalized) {
    return null;
  }

  const separatorIndex = normalized.indexOf(':');
  if (separatorIndex > 0) {
    const key = normalized.slice(0, separatorIndex).trim();
    const tagValue = normalized.slice(separatorIndex + 1).trim();
    if (key || tagValue) {
      return { key: key || 'Tag', value: tagValue };
    }
  }

  return { key: 'Tag', value: normalized };
}

function getTagRecords(
  value: unknown,
  label: string,
): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (isRecord(item)) {
          return item;
        }
        return parsePrimitiveTagValue(item);
      })
      .filter((tag): tag is Record<string, unknown> => Boolean(tag));
  }

  if (typeof value === 'string') {
    const parsed = safeJsonParse<unknown>(value, label);
    if (Array.isArray(parsed)) {
      return getTagRecords(parsed, label);
    }
    if (isRecord(parsed)) {
      return Object.entries(parsed).map(([key, val]) => ({
        key,
        value: val,
      }));
    }
    const primitiveTag = parsePrimitiveTagValue(value);
    return primitiveTag ? [primitiveTag] : [];
  }

  if (isRecord(value)) {
    return Object.entries(value).map(([key, val]) => ({
      key,
      value: val,
    }));
  }

  return [];
}

export function getTags(value: unknown, label: string): Tag[] {
  return getTagRecords(value, label)
    .map((tag) => {
      const key = getString(tag.Key) ?? getString(tag.key);
      const val = stringifyTagValue(tag.Value ?? tag.value);
      if (!key && !val) {
        return null;
      }
      return { key: key ?? 'Tag', value: val ?? '' };
    })
    .filter((tag): tag is Tag => Boolean(tag));
}

function hasTagData(value: unknown, label: string): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => hasTagData(item, label));
  }
  if (isRecord(value)) {
    return Object.keys(value).length > 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }
    const parsed = safeJsonParse<unknown>(trimmed, label);
    if (parsed !== null && parsed !== undefined && parsed !== trimmed) {
      return hasTagData(parsed, label);
    }
    return true;
  }
  return true;
}

export function getFirstTagSource(
  label: string,
  ...values: unknown[]
): unknown {
  for (const value of values) {
    if (hasTagData(value, label)) {
      return value;
    }
  }
  return undefined;
}

/**
 * Split a summary string around the first occurrence of `assetName`
 * (case-insensitive). Returns `null` when no match is found or when
 * `assetName` is empty/undefined so callers can fall back to plain text.
 */
export function splitByAssetName(
  summary: string,
  assetName: string | undefined,
): { before: string; match: string; after: string } | null {
  if (!assetName) {
    return null;
  }
  const idx = summary.toLowerCase().indexOf(assetName.toLowerCase());
  if (idx === -1) {
    return null;
  }
  return {
    before: summary.slice(0, idx),
    match: summary.slice(idx, idx + assetName.length),
    after: summary.slice(idx + assetName.length),
  };
}
